import { useQuery } from "react-query";

import { getSwapPath } from "../../../../api/swap";
import { useChains } from "../../../../components/FetchConfig";
import { useAlerting, useTranslation } from "../../../../hooks";
import { useAppSelector } from "../../../../reducer/hooks";
import { selectProfile } from "../../../../reducer/profileSlice";
import { findDecimalByChainNameAndAsset } from "../../../../utils/helper";
import Button from "../components/Button";
import SwapActions from "../components/SwapActions";
import { useCreateSwapRequestContext } from "../context";
import { useFormFields } from "../hooks";
import { useExactPayQuote } from "../hooks/useExactPayQuote";
import { useExactPaySwap } from "../hooks/useExactPaySwap";
import { TabTypes } from "../types";
import AmountTextField from "./components/AmountTextField";
import Balance from "./components/Balance";
import QuoteDetails from "./components/QuoteDetails";
import SmartAccountType from "./components/SmartAccountType";
import WalletAddressField from "./components/WalletAddressField";
import ContentContainer from "./layout/ContentContainer";
import ContentRow from "./layout/ContentRow";
import HeaderRow from "./layout/HeaderRow";
import InnerContent from "./layout/InnerContent";

const ExactPayContent = () => {
  const {
    filterFields,
    exactPayFields,
    setExactPayFields,
    resetEverything,
    swapPaths,
    setSwapPaths,
  } = useCreateSwapRequestContext();

  const { t } = useTranslation("createSwapRequest");
  const { alerting } = useAlerting();

  const { selectChainByPieces } = useChains();
  const { merchantId } = useAppSelector(selectProfile);
  const { chain_type, chain_id } = selectChainByPieces({
    chain_name: filterFields.chainName,
  });

  const { updateWalletAddress, updateAmount } = useFormFields({
    fields: exactPayFields,
    setFields: setExactPayFields,
    quoteFields: ["estimateAmount", "exchangeRate", "getMin", "quotedTime"],
  });

  const fromCurrencyDecimals = Number(
    findDecimalByChainNameAndAsset(
      String(chain_type),
      String(chain_id),
      filterFields.fromCurrency
    )
  );

  const { handleQuote } = useExactPayQuote({ swapPaths });

  const { handleSwap } = useExactPaySwap({
    swapPaths,
  });

  const handleReset = () => {
    resetEverything(true);
  };

  useQuery({
    queryKey: ["swap-path", chain_type, chain_id],
    queryFn: () => {
      return getSwapPath({
        chainType: Number(chain_type),
        chainId: Number(chain_id),
      });
    },
    enabled: Boolean(chain_type && chain_id),
    onSuccess: (data) => {
      setSwapPaths(data.rows);
    },
  });

  return (
    <ContentContainer>
      <HeaderRow>{t("exactPay")}</HeaderRow>
      <InnerContent>
        <div className="split">
          <div className="form">
            <ContentRow title={t("walletAddress")}>
              <WalletAddressField
                addressBookOrder={exactPayFields.addressBookOrder}
                chainName={filterFields.chainName}
                onFieldChange={updateWalletAddress}
              />
            </ContentRow>

            <ContentRow title={t("smartAccountType")}>
              <SmartAccountType fields={exactPayFields} />
            </ContentRow>

            <ContentRow title={t("balance")}>
              <Balance
                chainType={Number(chain_type)}
                chainId={Number(chain_id)}
                assetName={filterFields.fromCurrency}
                decimals={String(fromCurrencyDecimals)}
                walletAddress={exactPayFields.rawWalletInfo?.wallet_address}
                balance={exactPayFields.balance}
                updateBalance={(balance: string) => {
                  setExactPayFields({ ...exactPayFields, balance });
                }}
              />
            </ContentRow>

            <ContentRow title={t("exactPayAmount")}>
              <AmountTextField
                label={t("peExactPayAmount")}
                value={exactPayFields.exactPayAmount}
                onChange={(value) => updateAmount(value, "exactPayAmount")}
                currency={filterFields.fromCurrency}
                decimalLimit={fromCurrencyDecimals}
              />
            </ContentRow>

            <div className="quote-btn">
              <Button onClick={handleQuote}>{t("quote")}</Button>
            </div>
          </div>

          <QuoteDetails
            fields={exactPayFields}
            t={t}
            estimateField="estimateAmount"
            minMaxField="getMin"
            estimateLabel="estimateGetAmount"
            minMaxLabel="getMin"
            mode={TabTypes.EXACT_PAY}
            amountCurrency={filterFields.toCurrency}
            amountFormulaString={t("minGetAmountFormula", {
              maxSlippage: filterFields.maxSlippage,
            })}
          />
        </div>
      </InnerContent>

      <SwapActions onSwap={handleSwap} onReset={handleReset} />
    </ContentContainer>
  );
};

export default ExactPayContent;
